import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, retryOperation } from '../../lib/supabase';
import { Loader2, Mail, AlertCircle } from 'lucide-react';
import Logo from '../Logo';

interface LocationState {
  email?: string;
  mode?: string;
  message?: string;
}

export default function AuthForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
  });

  // Set initial values from location state
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.email) {
      setFormData(prev => ({ ...prev, email: state.email || '' }));
    }
    if (state?.message) {
      setMessage(state.message);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: magicLinkError } = await retryOperation(() =>
        supabase.auth.signInWithOtp({
          email: formData.email.toLowerCase(),
          options: {
            emailRedirectTo: `${window.location.origin}/magic-link-callback`,
          }
        })
      );

      if (magicLinkError) throw magicLinkError;

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="flex justify-center mb-8">
        <Logo />
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md">
        {success ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">Magic link sent!</p>
            </div>
            <p className="text-gray-600">
              We've sent a magic link to your email. Click the link to sign in and access your garden.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-center mb-6">Sign in with Magic Link</h2>

            {message && (
              <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-md text-primary-700 text-sm">
                {message}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Send Magic Link'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}